import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

// ─── Formspree ───────────────────────────────────────────────────────────
// 1. Go to https://formspree.io  →  Sign up (free)
// 2. Create a new form  →  copy the form ID (e.g. "xwpkgjqz")
// 3. Replace the placeholder below with your real ID
// Submissions will be emailed directly to the address you register with.
const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID || 'YOUR_FORMSPREE_ID';
const FORMSPREE_URL = `https://formspree.io/f/${FORMSPREE_ID}`;

const LOCATION_TYPES = [
  'Office', 'Apartment Complex', 'Gym', 'School',
  'Medical Facility', 'Retail', 'Warehouse', 'Hotel', 'Other',
];

const sectionVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const Contact = () => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    locationType: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'First name is required';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required';
    if (!form.email.trim()) {
      errs.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errs.email = 'Please enter a valid email';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          'First Name': form.firstName.trim(),
          'Last Name': form.lastName.trim(),
          email: form.email.trim(),
          Phone: form.phone.trim(),
          'Location Type': form.locationType || 'Other',
          Message: form.message.trim(),
        }),
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success("Request submitted! We'll be in touch within 24 hours.");
      } else {
        const data = await res.json().catch(() => null);
        toast.error(data?.error || 'Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 bg-kv-mist py-16 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          className="text-center mb-10 sm:mb-14"
        >
          <span className="text-xs uppercase tracking-[0.22em] text-kv-gold font-sans font-semibold mb-4 block">
            Get Started
          </span>
          <h2 className="text-3xl sm:text-4xl font-display tracking-tight text-kv-black-2">
            Ready to put a machine in your space?
          </h2>
        </motion.div>

        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="max-w-2xl mx-auto"
        >
          {submitted ? (
            <div className="rounded-2xl bg-white border border-black/8 p-8 sm:p-12 shadow-[0_1px_0_rgba(17,17,20,0.04),0_12px_35px_rgba(17,17,20,0.06)] text-center">
              <CheckCircle2 className="h-12 w-12 text-kv-gold mx-auto mb-4" />
              <h3 className="text-2xl font-display text-kv-black-2 mb-3">Thank You!</h3>
              <p className="text-gray-500 font-sans mb-6">
                We've received your request and will get back to you within 24 hours.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm({ firstName: '', lastName: '', email: '', phone: '', locationType: '', message: '' }); }}
                className="rounded-full bg-kv-black text-white hover:bg-kv-black-2 px-6 py-2.5 text-sm font-medium transition-colors"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="rounded-2xl bg-white border border-black/8 p-6 sm:p-10 shadow-[0_1px_0_rgba(17,17,20,0.04),0_12px_35px_rgba(17,17,20,0.06)]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="firstName" className="text-sm font-medium text-kv-black-2 mb-1.5 block font-sans">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    data-testid="contact-first-name-input"
                    value={form.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    placeholder="John"
                    className={`font-sans ${errors.firstName ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                  />
                  {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-sm font-medium text-kv-black-2 mb-1.5 block font-sans">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    data-testid="contact-last-name-input"
                    value={form.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    placeholder="Doe"
                    className={`font-sans ${errors.lastName ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                  />
                  {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-kv-black-2 mb-1.5 block font-sans">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="john@company.com"
                    className={`font-sans ${errors.email ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                  />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-kv-black-2 mb-1.5 block font-sans">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    data-testid="contact-phone-input"
                    value={form.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="(210) 555-0123"
                    className="font-sans"
                  />
                </div>
              </div>

              <div className="mb-4">
                <Label htmlFor="locationType" className="text-sm font-medium text-kv-black-2 mb-1.5 block font-sans">Location Type</Label>
                <Select
                  value={form.locationType}
                  onValueChange={(val) => handleChange('locationType', val)}
                >
                  <SelectTrigger data-testid="contact-location-type-select" className="font-sans">
                    <SelectValue placeholder="Select your location type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPES.map((type) => (
                      <SelectItem key={type} value={type} className="font-sans">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label htmlFor="message" className="text-sm font-medium text-kv-black-2 mb-1.5 block font-sans">Message</Label>
                <textarea
                  id="message"
                  name="message"
                  data-testid="contact-message-textarea"
                  value={form.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Tell us about your space, foot traffic, or any questions..."
                  rows={4}
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 font-sans resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="w-full rounded-full bg-kv-black text-white hover:bg-kv-black-2 active:scale-[0.99] px-6 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>
                ) : (
                  'Get Your Free Machine'
                )}
              </button>
            </form>
          )}

          {/* Email */}
          <div className="flex items-center justify-center mt-8">
            <a
              href="mailto:info@keylessvending.com"
              data-testid="contact-email-link"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-kv-black-2 transition-colors font-sans"
            >
              <Mail className="h-4 w-4" />
              info@keylessvending.com
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
